import { useEffect, useState } from "react";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { updateManufacturingOverheadBucket } from "@/lib/manufacturingClient";

function formatUsd(value: number): string {
  return value.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export function OverheadPeriodDebtEdit({
  bucketId,
  totalUsd,
  disabled,
  onSaved,
}: {
  bucketId: string;
  totalUsd: number | null;
  disabled: boolean;
  onSaved: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(totalUsd == null ? "" : String(totalUsd));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValue(totalUsd == null ? "" : String(totalUsd));
  }, [totalUsd]);

  async function save() {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setError("Total debt must be zero or more.");
      return;
    }
    setBusy(true);
    setError(null);
    const result = await updateManufacturingOverheadBucket({
      bucket_id: bucketId,
      balance_start_usd: parsed,
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setEditing(false);
    await onSaved();
  }

  return (
    <div className="mt-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-gray-600 dark:text-zinc-400">
          Total debt {totalUsd == null ? "not set" : formatUsd(totalUsd)}
        </span>
        <button
          type="button"
          className={ghostBtnClass}
          disabled={disabled || busy}
          onClick={() => {
            setError(null);
            setEditing((open) => !open);
          }}
        >
          Edit total debt
        </button>
      </div>
      {editing ? (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <label className="block text-xs text-gray-600 dark:text-zinc-400">
            Total debt
            <input
              type="number"
              min={0}
              step="0.01"
              className="mt-1 w-40 rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              value={value}
              onChange={(event) => setValue(event.target.value)}
              disabled={disabled || busy}
            />
          </label>
          <button type="button" className={ghostBtnClass} disabled={disabled || busy} onClick={() => void save()}>
            {busy ? "Saving…" : "Save total"}
          </button>
        </div>
      ) : null}
      {error ? <p className="mt-1 text-xs text-red-700 dark:text-red-300">{error}</p> : null}
    </div>
  );
}
